import type { DigitalMirrorAuthConfig } from './client';

export type DigitalMirrorRuntimeConfig = {
  origin: string;
  auth: DigitalMirrorAuthConfig;
};

export type DigitalMirrorConfigResult =
  | { ok: true; config: DigitalMirrorRuntimeConfig }
  | { ok: false; error: string };

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const normalizeOrigin = (raw: string): string | null => {
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
};

export function getDigitalMirrorConfigResult(): DigitalMirrorConfigResult {
  const w = window as any;

  const originRaw = w?.__DIGITAL_MIRROR_ORIGIN__;
  const origin = isNonEmptyString(originRaw) ? normalizeOrigin(originRaw) : null;
  if (!origin) {
    return {
      ok: false,
      error:
        'Digital Mirror origin is missing. Configure window.__DIGITAL_MIRROR_ORIGIN__ in digital-mirror.runtime-config.js (e.g. "https://mirror-app.example.com").',
    };
  }

  const authRaw = w?.__DIGITAL_MIRROR_AUTH__;
  if (!isRecord(authRaw)) {
    return {
      ok: false,
      error:
        'Digital Mirror auth is missing. Configure window.__DIGITAL_MIRROR_AUTH__ in digital-mirror.runtime-config.js.',
    };
  }

  const userId = authRaw.userId;
  const password = authRaw.password;
  const storeId = authRaw.storeId;
  const deviceId = authRaw.deviceId;

  if (!isNonEmptyString(userId) || !isNonEmptyString(password) || !isNonEmptyString(storeId) || !isNonEmptyString(deviceId)) {
    return {
      ok: false,
      error:
        'Digital Mirror auth is invalid. Ensure __DIGITAL_MIRROR_AUTH__ has non-empty userId/password/storeId/deviceId in digital-mirror.runtime-config.js.',
    };
  }

  return {
    ok: true,
    config: {
      origin,
      auth: {
        userId: userId.trim(),
        password: password.trim(),
        storeId: storeId.trim(),
        deviceId: deviceId.trim(),
      },
    },
  };
}
